/**
 * API Client Configuration
 * Provides configured instances of NSwag-generated API clients with MSAL authentication.
 * Manager-UX uses corporate Azure AD authentication only (no B2C).
 */

import { IPublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser';
import { Client, IConfig } from '../../generated/api.onboarding.client';
import { apiConfig, getLoginRequest, isTestMode } from '../../config';

export interface PersonSearchRequestBody {
    input: string;
    Aliases: string[];
}

export interface PersonSearchResponseItem {
    userPrincipalName?: string;
    organizationAliasHierarchy?: string;
    preferredFirstName?: string;
    preferredLastName?: string;
}

/**
 * Creates an IConfig implementation for test mode (no authentication)
 */
export const createTestApiConfig = (): IConfig => {
    return {
        getAuthorization: async (): Promise<string> => {
            return 'test-mock-token';
        },
        AdditionalHeaders: {
            'Content-Type': 'application/json',
        },
    };
};

/**
 * Creates an IConfig implementation that uses MSAL corporate authentication
 */
export const createApiConfig = (
    msalInstance: IPublicClientApplication,
    account: AccountInfo
): IConfig => {
    return {
        getAuthorization: async (): Promise<string> => {
            try {
                const loginReq = getLoginRequest();
                const apiScopes =
                    loginReq.scopes && loginReq.scopes.length > 0 ? loginReq.scopes : [];

                if (apiScopes.length === 0) {
                    console.warn('No API scopes configured! Using ID token as fallback.');
                    const idTokenResponse = await msalInstance.acquireTokenSilent({
                        account: account,
                        scopes: ['openid', 'profile'],
                    });
                    return idTokenResponse.idToken;
                }

                const silentRequest: SilentRequest = {
                    scopes: apiScopes,
                    account: account,
                    forceRefresh: false,
                };

                const response = await msalInstance.acquireTokenSilent(silentRequest);

                const token = response.accessToken || response.idToken;
                if (!token) {
                    throw new Error('No token received from MSAL');
                }

                return token;
            } catch (error) {
                console.error('Failed to acquire token silently:', error);

                // If silent acquisition fails, trigger interactive login
                const loginReq = getLoginRequest();
                await msalInstance.acquireTokenRedirect({
                    scopes: loginReq.scopes || [],
                });
                throw error;
            }
        },
        AdditionalHeaders: {
            'Content-Type': 'application/json',
        },
    };
};

/**
 * Creates a configured Client instance for the Onboarding API.
 * In test mode, uses mock config; otherwise uses MSAL corporate authentication.
 */
export const createApiClient = (
    msalInstance?: IPublicClientApplication | null,
    account?: AccountInfo | null
): Client => {
    if (isTestMode || !msalInstance || !account) {
        const config = createTestApiConfig();
        return new Client(config, apiConfig.baseUrl);
    }
    const config = createApiConfig(msalInstance, account);
    return new Client(config, apiConfig.baseUrl);
};

/**
 * Calls PersonSearch API with UPN query and alias filters.
 */
export const searchPersons = async (
    body: PersonSearchRequestBody,
    msalInstance?: IPublicClientApplication | null,
    account?: AccountInfo | null,
): Promise<PersonSearchResponseItem[]> => {
    if (!body.input?.trim()) {
        return [];
    }

    const config = isTestMode || !msalInstance || !account
        ? createTestApiConfig()
        : createApiConfig(msalInstance, account);

    const authorization = await config.getAuthorization();
    const token = authorization.toLowerCase().startsWith('bearer')
        ? authorization
        : `Bearer ${authorization}`;

    const response = await fetch(`${apiConfig.baseUrl}/api/PersonSearch/search`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(config.AdditionalHeaders || {}),
            Authorization: token,
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error(`PersonSearch failed with status ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data as PersonSearchResponseItem[] : [];
};
